import { Box, Button, Grid, Paper, TextField, TextFieldProps, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import moment from "moment";
import { Key, useState } from "react";
import { useEffect } from "react";
import { toast } from "react-toastify";

import ResponseModel from "../../models/ResponseModel";
import { addTerminalOwnerGridKeys, TerminalGridKey, TerminalModel } from "../../models/TerminalModel";
import { TerminalRequestModel } from "../../models/TerminalRequestModel";
import TerminalService from "../../services/terminals/TerminalService";
import { hasApiError } from "../../utils/Utils";

import { style } from "./AddTerminalOwner.style";

import "moment/locale/hu";

type AddTerminalOwnerProps = {
  getTerminalRequests: () => Promise<void>;
  request: TerminalRequestModel | null;
  setOwnerSetterOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

// -----------------------------------------------------------------Exports---
const AddTerminalOwner = (props: AddTerminalOwnerProps): JSX.Element => {
  const [request, setRequest] = useState<TerminalRequestModel>((props.request ?? {}) as TerminalRequestModel);
  const [selectedTerminal, setSelectedTerminal] = useState<TerminalModel | undefined>(undefined);
  const [date, setDate] = useState<Date>(new Date());

  useEffect((): void => {
    void getTerminals();
  }, []);

  const getTerminals = async (): Promise<void> => {
    const response: ResponseModel = await TerminalService.getTerminals({ tidCode: request.tidCode ?? 0 });

    //showing the result
    if (hasApiError(response)) return;
    else {
      setSelectedTerminal(
        (response.data as TerminalModel[]).find(
          (terminal: TerminalModel): boolean =>
            terminal.tidCode === (request.tidCode ?? 0) && terminal.terminalCode === request.terminalCode,
        ),
      );
    }
  };

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string): void => {
    const newRequest: TerminalRequestModel = { ...request };
    (newRequest as Record<string, unknown>)[key] = e.target.value;
    setRequest(newRequest);
  };

  const onDatePicker = (newDate: Date | ((prevState: Date) => Date) | null, _value: string | undefined): void => {
    if (newDate) setDate(newDate as Date);
  };

  const onRegisterButton = async (): Promise<void> => {
    //guards
    if (selectedTerminal === undefined) {
      toast.error("Nincs kiválasztva gyári szám");
      return;
    }
    if (moment(date) < moment().subtract(15, "d")) {
      toast.error("A kiválasztott nap nem lehet korábban, mint 15 nappal ezelőtt");
      return;
    }
    if (moment(date) > moment().add(15, "d")) {
      toast.error("A kiválasztott nap nem lehet később, mint 15 nappal ezután");
      return;
    }
    if (moment(date) < moment(request.tid?.createdAt)) {
      toast.error("A kiválasztott nap nem lehet korábban, mint az összerendelés napja");
      return;
    }

    //updating terminal state
    const loadingToastId = toast.loading("Kihelyezés folyamatban...");
    const response: ResponseModel = await TerminalService.terminalRelocation(selectedTerminal.terminalCode, {
      terminalContractStart: moment(date).format("YYYY-MM-DD"),
    });

    //showing the result
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      toast.update(loadingToastId, { render: "Sikeres kihelyezés", type: "success", isLoading: false, autoClose: 4000 });
      void props.getTerminalRequests();
      props.setOwnerSetterOpen(false);
    }
  };

  return (
    <Paper style={style.paper}>
      <Typography variant="h3" style={style.title}>
        Terminál kihelyezés
      </Typography>
      <Grid container spacing={4}>
        {addTerminalOwnerGridKeys
          .filter((gridKey: TerminalGridKey): boolean => gridKey.inputHidden !== true)
          .map((gridKey: TerminalGridKey, i: Key): JSX.Element => {
            return (
              <Grid item key={i} md={gridKey.md ?? 3} sm={gridKey.sm ?? 6} xs={gridKey.xs ?? 12}>
                <Typography style={style.gridLabel} align={gridKey.align ?? "left"}>
                  {gridKey.label}
                </Typography>
                <TextField
                  fullWidth
                  disabled={gridKey.inputDisabled ?? false}
                  value={(request as Record<string, unknown>)[gridKey.key] ?? ""}
                  variant="standard"
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                    onTextChange(e, gridKey.key)
                  }
                />
                {gridKey.mandatory && <Typography style={style.helper}>Kötelező mező*</Typography>}
              </Grid>
            );
          })}
        <Grid item xs={12} sm={6} md={3}>
          <Typography style={style.gridLabel} align={"left"}>
            Terminál gyári száma
          </Typography>
          <TextField fullWidth disabled={true} value={selectedTerminal?.serialNumber ?? ""} variant="standard" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="hu">
            <Typography style={style.gridLabel} align={"left"}>
              Kihelyezés ideje
            </Typography>
            <MobileDatePicker
              inputFormat="YYYY-MM-DD"
              toolbarTitle="Válassz egy dátumot"
              value={date}
              onChange={onDatePicker}
              renderInput={(params: TextFieldProps): JSX.Element => (
                <TextField {...params} fullWidth variant="standard" />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Box style={style.buttonBox}>
        <Button variant="contained" color="error" onClick={(): void => props.setOwnerSetterOpen(false)}>
          Vissza
        </Button>
        <Button
          variant="contained"
          onClick={(): void => {
            void onRegisterButton();
          }}
          style={style.button}
        >
          Mentés
        </Button>
      </Box>
    </Paper>
  );
};

export default AddTerminalOwner;
